import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

type NavRowProps = React.ComponentProps<"button"> & {
  icon?: LucideIcon;
  /** Right-aligned count, chevron or badge. */
  trailing?: React.ReactNode;
  active?: boolean;
  /** Left inset in px for nested rows (16 per level). */
  indent?: number;
  asChild?: boolean;
};

/**
 * A 32px navigation row for rail menus, the settings sidebar and the client
 * profile nav. Not a button style: rows are flush, `fill-1` on hover, and
 * `fill-1` with `text-fg` and an `fg` icon when active.
 */
function NavRow({
  icon: Icon,
  trailing,
  active = false,
  indent = 0,
  asChild = false,
  className,
  children,
  style,
  ...props
}: NavRowProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="nav-row"
      data-active={active || undefined}
      aria-current={active ? "page" : undefined}
      type={asChild ? undefined : "button"}
      style={indent ? { paddingLeft: 8 + indent, ...style } : style}
      className={cn(
        "flex h-8 w-full min-w-0 shrink-0 items-center gap-2 rounded-md px-2 text-left text-base text-fg-2 transition-colors duration-100 outline-none hover:bg-fill-1 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 data-active:bg-fill-1 data-active:text-fg [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {Icon ? (
            <Icon
              className={cn("size-4", active ? "text-fg" : "text-fg-3")}
              aria-hidden
            />
          ) : null}
          <span className="min-w-0 flex-1 truncate">{children}</span>
          {trailing ? (
            <span className="flex shrink-0 items-center text-sm text-fg-4">
              {trailing}
            </span>
          ) : null}
        </>
      )}
    </Comp>
  );
}

export { NavRow };
export type { NavRowProps };
